import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { UploadCloud, FolderOpen, Plus, Settings, CheckCircle2, AlertCircle } from 'lucide-react'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useFetchProjectsQuery, useGetProjectQuery } from '../../services/projectsApi'
import { useGetTagsQuery } from '../../services/tagApi'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { Project } from '../../types/models/project'
// no prefetch needed; images are fetched on-demand in resolver
import { projectsApi } from '../../services/projectsApi'
import { apiTags } from '../../services/apiTags'
import { contentApi } from '../../services/contentApi'
import { publishExportProgress } from '../../utils/exportProgressBus'
import { useNavigate } from 'react-router-dom'
import {
  clearFilterTags,
  setActiveDatasetSubView,
  setActiveInferenceSubView,
  setActiveModelSubView,
  setSelectedProjectIdAndClearPrediction,
} from '../../store/slices/projectSlice'
import {
  setDatasetSources,
  setTrainingSource,
  setValidationSource,
} from '../../store/slices/datasetSlice'
import { clearSelectedModelId } from '../../store/slices/modelSlice'
import { setAnnotatedPage, setUnannotatedPage } from '../../store/slices/annotationSlice'
import { useExportSnippets } from '../../hooks/useExportSnippets'
import TargetProjectSelector from './ExportSnippetsModal/TargetProjectSelector'
import ExportOptionsPanel from './ExportSnippetsModal/ExportOptionsPanel'

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 900px;
  }

  .modal-content {
    background: linear-gradient(145deg, #1a202c 0%, #2d3748 100%);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
    max-height: 95vh;
  }

  .modal-header {
    background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
    border-bottom: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 16px 16px 0 0;
    padding: 1.5rem 2rem;

    .modal-title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.5rem;
      font-weight: 600;
      color: #fff;
      margin: 0;

      svg {
        color: #667eea;
      }
    }

    .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
      opacity: 0.8;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    padding: 2rem;
    color: #fff;
    max-height: calc(95vh - 200px);
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(102, 126, 234, 0.3);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(102, 126, 234, 0.5);
    }
  }

  .modal-footer {
    background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
    border-top: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 0 0 16px 16px;
    padding: 1.5rem 2rem;
  }
`

const SectionCard = styled.div`
  background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  height: 100%;
`

const SectionTitle = styled.h5`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(102, 126, 234, 0.2);

  svg {
    color: #667eea;
  }
`

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 8px;
  margin-top: 1rem;

  .spinner-border {
    width: 1.25rem;
    height: 1.25rem;
    border-width: 2px;
  }

  span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.95rem;
  }
`

const SuccessAlert = styled(Alert)`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  color: #fff;
  padding: 1rem 1.25rem;
  margin: 0;
`

const ErrorAlert = styled(Alert)`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #fff;
  padding: 1rem 1.25rem;
  margin: 0;
`

// Workaround for TypeScript TS2590 error with styled-components and React-Bootstrap Button
const ButtonBase = Button as unknown as React.ComponentType<any>

const StyledButton = styled(ButtonBase)`
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: #fff;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.btn-outline-danger {
    border-color: rgba(239, 68, 68, 0.5);
    color: rgba(239, 68, 68, 0.9);

    &:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.7);
      color: #ef4444;
    }
  }
`

interface ExportSnippetsModalProps {
  show: boolean
  onHide: () => void
  // Back-compat for single-image use
  contentId?: string
  boxes?: AnnotationDataBoundingBox[]
  // New multi-image bulk support
  items?: Array<{ contentId: string; boxes: AnnotationDataBoundingBox[] }>
}

const ExportSnippetsModal: React.FC<ExportSnippetsModalProps> = ({
  show,
  onHide,
  contentId,
  boxes,
  items,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const sourceProjectId = useAppSelector(selectSelectedProjectId)
  const { data: sourceProject } = useGetProjectQuery(sourceProjectId || '', {
    skip: !sourceProjectId,
  })

  const { data: tagsResp } = useGetTagsQuery(
    {
      project_id: sourceProjectId || '',
      dataset_id: sourceProject?.datasetid || '',
    },
    { skip: !sourceProjectId || !sourceProject?.datasetid }
  )

  // Fetch projects to select an existing classification project
  const { data: projectsResp } = useFetchProjectsQuery({
    page: 0,
    limit: 200,
  })
  const classificationProjects: Project[] = useMemo(
    () =>
      (projectsResp?.projects || []).filter((p: Project) => p.annotation_type === 'classification'),
    [projectsResp]
  )

  const exportHook = useExportSnippets()

  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [targetProjectId, setTargetProjectId] = useState<string>('')
  const [newProjectName, setNewProjectName] = useState<string>('')
  const [imageSize, setImageSize] = useState<number>(512)
  const [error, setError] = useState<string>('')
  const [uploadAsUnannotated, setUploadAsUnannotated] = useState<boolean>(false)
  const [datasetSplit, setDatasetSplit] = useState<'training' | 'validation'>('training')
  const isOBBProject = sourceProject?.annotation_type === 'oriented_bounding_box'
  const [rotateLongSideVertical, setRotateLongSideVertical] = useState<boolean>(false)

  // Load the source image as a local object URL (blob) so canvas uses a same-origin URL
  const singleMode = !!contentId && !!boxes && (!items || items.length === 0)

  // Set default target project once projects load and no selection yet
  useEffect(() => {
    if (show && !targetProjectId && classificationProjects.length > 0) {
      // Prefer previously used project if available and valid
      const stored = localStorage.getItem('lastExportProjectId') || ''
      const validStored = stored && classificationProjects.some((p) => p.id === stored)
      setTargetProjectId(validStored ? stored : classificationProjects[0].id)
    }
  }, [show, classificationProjects, targetProjectId])

  // Persist last selected export project
  useEffect(() => {
    if (targetProjectId) {
      try {
        localStorage.setItem('lastExportProjectId', targetProjectId)
      } catch {}
    }
  }, [targetProjectId])

  const onExport = async () => {
    try {
      setError('')
      console.log('[ExportSnippetsModal] start export clicked')
      if (!sourceProjectId || !sourceProject?.datasetid) {
        throw new Error('Source project not ready')
      }
      const exportItems =
        items && items.length > 0 ? items : contentId && boxes ? [{ contentId, boxes }] : []
      console.log('[ExportSnippetsModal] prepared exportItems', {
        items: exportItems.length,
        totalBoxes: exportItems.reduce((acc, it) => acc + it.boxes.length, 0),
      })
      // For single image exports, open the toast expanded initially
      if (singleMode) {
        try {
          publishExportProgress({ visible: true, minimized: false })
        } catch {}
      }
      // Fire-and-forget: start export and immediately close the modal
      void exportHook.startExport({
        mode,
        targetProjectId,
        newProjectName,
        datasetSplit,
        uploadAsUnannotated,
        imageSize,
        rotateLongSideVertical: isOBBProject ? rotateLongSideVertical : false,
        items: exportItems,
        resolveImageObjectUrl: async (cid: string) => {
          console.time(`[resolveImageObjectUrl] ${cid}`)
          const res = await dispatch(
            contentApi.endpoints.getContentImage.initiate({
              ContentID: cid,
              project_id: sourceProjectId,
              dataset_id: sourceProject?.datasetid,
            })
          )
          console.timeEnd(`[resolveImageObjectUrl] ${cid}`)
          if ('data' in res && typeof res.data === 'string') return res.data
          throw new Error('Failed to load image')
        },
        classificationProjects,
        tags: tagsResp?.tags,
        sourceProjectId: sourceProjectId,
        sourceDatasetId: sourceProject?.datasetid,
      })
      // Close immediately; progress continues in the toast
      onHide()
    } catch (error: unknown) {
      console.error(error)
      const message =
        typeof error === 'object' && error && 'message' in error
          ? String((error as { message: unknown }).message)
          : 'Export failed'
      setError(message)
    }
  }

  const totalSelected =
    items && items.length > 0
      ? items.reduce((acc, it) => acc + it.boxes.length, 0)
      : boxes
      ? boxes.length
      : 0
  const canExport = useMemo(() => {
    const hasSelection = totalSelected > 0
    if (mode === 'new') return newProjectName.trim().length > 0 && hasSelection
    return !!targetProjectId && hasSelection
  }, [mode, newProjectName, targetProjectId, totalSelected])

  const canStart =
    canExport &&
    (exportHook.status === 'idle' ||
      exportHook.status === 'error' ||
      exportHook.status === 'success')

  useEffect(() => {
    try {
      console.log('[ExportSnippetsModal] state', {
        totalSelected,
        mode,
        targetProjectId,
        hasTarget: !!targetProjectId,
        newProjectName,
        canExport,
        canStart,
        status: exportHook.status,
      })
    } catch {}
  }, [totalSelected, mode, targetProjectId, newProjectName, canExport, canStart, exportHook.status])

  return (
    <StyledModal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <UploadCloud size={20} />
          Export to Classification Project
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          {error && (
            <ErrorAlert className="d-flex align-items-center gap-2">
              <AlertCircle size={18} />
              <span>{error}</span>
            </ErrorAlert>
          )}

          <Row className="g-3">
            <Col md={6}>
              <SectionCard>
                <SectionTitle>
                  <FolderOpen size={18} />
                  Target Project
                </SectionTitle>
                <TargetProjectSelector
                  mode={mode}
                  setMode={setMode}
                  classificationProjects={classificationProjects}
                  targetProjectId={targetProjectId}
                  setTargetProjectId={setTargetProjectId}
                  newProjectName={newProjectName}
                  setNewProjectName={setNewProjectName}
                />
              </SectionCard>
            </Col>
            <Col md={6}>
              <SectionCard>
                <SectionTitle>
                  <Settings size={18} />
                  Export Options
                </SectionTitle>
                <ExportOptionsPanel
                  datasetSplit={datasetSplit}
                  setDatasetSplit={setDatasetSplit}
                  uploadAsUnannotated={uploadAsUnannotated}
                  setUploadAsUnannotated={setUploadAsUnannotated}
                  imageSize={imageSize}
                  setImageSize={setImageSize}
                  selectedCount={totalSelected}
                  isOBBProject={isOBBProject}
                  rotateLongSideVertical={rotateLongSideVertical}
                  setRotateLongSideVertical={setRotateLongSideVertical}
                />
              </SectionCard>
            </Col>
          </Row>

          {(exportHook.status === 'creating' ||
            exportHook.status === 'preparing' ||
            exportHook.status === 'uploading') && (
            <StatusContainer>
              <Spinner animation="border" size="sm" variant="primary" />
              <span>
                {exportHook.status === 'creating' && 'Creating project...'}
                {exportHook.status === 'preparing' && 'Preparing images and labels...'}
                {exportHook.status === 'uploading' && 'Uploading snippets...'}
              </span>
            </StatusContainer>
          )}

          {exportHook.status === 'success' && (
            <SuccessAlert className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CheckCircle2 size={18} />
                <span>
                  Export complete: {exportHook.resultCount} image
                  {exportHook.resultCount !== 1 ? 's' : ''} uploaded.
                </span>
              </div>
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  const pid = exportHook.finalTargetProjectId
                  if (pid && pid.length > 0) {
                    try {
                      // Clear navigation-related state to mirror ProjectCard behavior
                      localStorage.removeItem('activeTab')
                      localStorage.removeItem('selectedModelTab')
                      localStorage.removeItem('activeDatasetSubView')
                      localStorage.removeItem('activeInferenceSubView')
                      localStorage.removeItem('activeModelSubView')
                      localStorage.removeItem('inferenceLoop_currentPhase')
                      localStorage.removeItem('compareModelId')

                      dispatch(setTrainingSource([]))
                      dispatch(setValidationSource([]))
                      dispatch(setDatasetSources([]))
                      dispatch(clearFilterTags())
                      dispatch(setSelectedProjectIdAndClearPrediction(pid))
                      dispatch(clearSelectedModelId(pid))
                      dispatch(setActiveInferenceSubView('Overview'))
                      dispatch(setActiveDatasetSubView('Overview'))
                      dispatch(setActiveModelSubView('Overview'))
                      // Reset pages in DatasetLabelingView
                      dispatch(setAnnotatedPage(1))
                      dispatch(setUnannotatedPage(1))
                    } catch {}
                    navigate(`/overview/${pid}/overview`)
                    setTimeout(() => {
                      try {
                        dispatch(projectsApi.util.invalidateTags([apiTags.Project]))
                      } catch {}
                    }, 1000)
                  } else {
                    navigate('/projects')
                  }
                }}
              >
                {`Go to ${exportHook.finalTargetProjectName || 'project'}`}
              </Button>
            </SuccessAlert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end gap-2">
        <StyledButton
          variant="outline-danger"
          onClick={onHide}
          disabled={
            exportHook.status === 'creating' ||
            exportHook.status === 'preparing' ||
            exportHook.status === 'uploading'
          }
        >
          Close
        </StyledButton>
        <StyledButton
          variant="primary"
          onClick={onExport}
          disabled={!canStart}
          title={exportHook.status === 'idle' ? undefined : 'Export in progress'}
        >
          <UploadCloud size={18} />
          Start Export
        </StyledButton>
      </Modal.Footer>
    </StyledModal>
  )
}

export default ExportSnippetsModal
